import { nestClient, type NestClient, type NestRequestOptions } from "../nest/client"
import type {
    ScheduleFollowupCancelAllParams,
    ScheduleFollowupCancelAllResult,
    ScheduleFollowupCancelParams,
    ScheduleFollowupCancelResult,
    ScheduleFollowupCreateParams,
    ScheduleFollowupCreateResult,
    ScheduleFollowupListParams,
    ScheduleFollowupListResult,
    ScheduleFollowupRunNowParams,
    ScheduleFollowupRunNowResult,
    ScheduleFollowupUpdateParams,
    ScheduleFollowupUpdateResult,
    SessionTurnStartParams,
    SessionTurnStartResult,
    TurnOptions,
} from "../nest/generated"

interface TaskBinding {
    taskId: string
    workspaceId: string
    sessionId: string
}

export interface WildLarkPromptComposeParams {
    voiceController?: boolean
    clientPreamble?: string
    designatedWorktreePath?: string
}

export interface WildLarkPromptComposeResult {
    appendSystemPrompt: string
}

export type WildLarkTaskTurnStartParams = SessionTurnStartParams &
    Pick<TaskBinding, "taskId" | "workspaceId"> & {
        prompt?: WildLarkPromptComposeParams
    }

export type WildLarkTaskCloseParams = TaskBinding

export type WildLarkTaskHandoffParams = TaskBinding & {
    thinking?: import("../agentTarget").ThinkingLevel
    harnessId: string
    model: string
    instructions?: string
    clientRequestId: string
    options: Pick<TurnOptions, "permissionMode" | "clientTools">
}

export interface WildLarkTaskCloseResult {
    taskId: string
    closed: true
    alreadyClosed: boolean
}

export type WildLarkTaskSleepParams = TaskBinding & { until: string }
export interface WildLarkTaskAttentionResult {
    taskId: string
    attention: import("../persistence/records").TaskAttention
}

export type WildLarkTaskScheduledStartReconcileParams = TaskBinding & {
    scheduleId?: string
    startedAt: string
}

export interface WildLarkTaskScheduledStartReconcileResult {
    taskId: string
    reopened: boolean
}

export type WildLarkTaskDeleteParams = Pick<TaskBinding, "taskId" | "workspaceId">

export interface WildLarkTaskDeleteResult {
    taskId: string
    deleted: boolean
    requested: number
    succeeded: number
    failed: number
    errors: string[]
}

export type WildLarkTaskDirectorySetParams = TaskBinding & {
    path: string
}

export interface WildLarkTaskDirectorySetResult {
    taskId: string
    workingDir: string
    sessionId: string
    displayOnly: boolean
}

export interface WildLarkExternalLink {
    id: string
    provider: "github" | "gitlab"
    kind: "pull_request" | "merge_request"
    host: string
    owner: string
    repository: string
    number: number
    url: string
    providerNodeId?: string
    watch: "active" | "unsupported"
    linkedAt: string
}

export type WildLarkTaskLinkSetParams = TaskBinding & {
    url: string
    providerNodeId?: string
    options?: TurnOptions
}

export type WildLarkTaskLinkRemoveParams = TaskBinding & { linkId: string }
export type WildLarkTaskLinkListParams = TaskBinding

export interface WildLarkTaskLinkMutationResult {
    taskId: string
    status: "complete" | "done-with-gaps"
    link: WildLarkExternalLink
    requested: number
    succeeded: number
    failed: number
    firstErrors: string[]
}

export interface WildLarkTaskLinkListResult {
    taskId: string
    links: WildLarkExternalLink[]
}

export interface WildLarkPullRequestSnapshot {
    number: number
    url: string
    title: string
    state: "open" | "closed" | "merged" | "unknown"
    draft: boolean
    reviewDecision: "approved" | "changes_requested" | "review_required" | "not_required" | "pending"
    checks: "passing" | "failing" | "pending" | "none"
    mergeStateStatus: "behind" | "blocked" | "clean" | "dirty" | "draft" | "has_hooks" | "unstable" | "unknown"
    headSha: string
    updatedAt?: string
    feedbackObserved?: boolean
    feedbackAt?: string
    checkedAt: string
    autoMergeEnabled?: boolean
    mergeQueue?: boolean
}

export type WildLarkTaskPullRequestReadParams = TaskBinding

export interface WildLarkPullRequestAutomation {
    v: 1
    linkId: string
    since: string
    phase: "watching" | "queued" | "running" | "attention"
    delivery?: { id: string; kind: string; operation: "repair" | "landing" | "closeout" }
    hold?: { reason: string; source: "snapshot" | "delivery" | "migration"; detail?: string; since: string }
    updatedAt: string
}

export interface WildLarkTaskPullRequestReadResult {
    taskId: string
    link: WildLarkExternalLink
    snapshot: WildLarkPullRequestSnapshot
    automation?: WildLarkPullRequestAutomation
}

export type WildLarkTaskPullRequestAutomationSetParams = TaskBinding & {
    options?: TurnOptions
}
export type WildLarkTaskPullRequestAutomationTargetParams = TaskBinding

export interface WildLarkTaskPullRequestAutomationResult {
    taskId: string
    status: "complete" | "done-with-gaps"
    requested: number
    succeeded: number
    failed: number
    firstErrors: string[]
    link: WildLarkExternalLink
    snapshot: WildLarkPullRequestSnapshot
    automation?: WildLarkPullRequestAutomation
}

export interface WildLarkTaskPullRequestAutomationStopResult {
    taskId: string
    status: "complete" | "done-with-gaps"
    stopped: boolean
    requested: number
    succeeded: number
    failed: number
    firstErrors: string[]
}

export interface WildLarkExternalWatchStatusResult {
    status: {
        state: "idle" | "watching" | "degraded"
        authState?: string
        lastPollAt?: string
        nextPollAt?: string
        requested: number
        succeeded: number
        failed: number
        firstErrors: string[]
        rateLimitCost?: number
        rateLimitRemaining?: number
        rateLimitResetAt?: string
    }
}

export type WildLarkTaskSessionsListParams = TaskBinding

export interface WildLarkTaskSessionManifestEntry {
    thinking?: import("../agentTarget").ThinkingLevel
    predecessorSessionId?: string
    role?: string
    label?: string
    parentSessionId?: string
    sessionId?: string
    providerId?: string
    providerSessionId?: string
    workspaceId?: string
    sessionFile?: string
    sessionRoot?: string
    cwd?: string
    worktreeId?: string
    executionDirectory?: string
    mode?: string
    model?: string
    status?: string
    activeTurnId?: string
    queuedTurnIds?: string[]
    eventCount?: number
    lastInputPreview?: string
    lastEventPreview?: string
    lastTurnId?: string
    createdAt?: string
    updatedAt?: string
    current: boolean
    error?: string
}

export type WildLarkTaskHarnessesListParams = TaskBinding

export interface WildLarkTaskHarnessModel {
    modelId: string
    selector?: string
    fullId?: string
    label?: string
    displayClass?: string
    tier?: string
    legacy?: boolean
    default?: boolean
    latest?: boolean
    nativeProvider?: string
    status?: string
    releaseDate?: string
    capabilities?: {
        reasoning: boolean
        attachments: boolean
        images: boolean
        pdfs: boolean
        tools: boolean
    }
    pricing?: {
        inputPerMillionUsd?: number
        outputPerMillionUsd?: number
        cacheReadPerMillionUsd?: number
        cacheWritePerMillionUsd?: number
        tiered?: boolean
    }
    limits?: {
        contextTokens?: number
        inputTokens?: number
        outputTokens?: number
    }
    variants?: string[]
}

export interface WildLarkTaskHarness {
    harnessId: string
    label: string
    installed: boolean
    available: boolean
    defaultModel?: string
    models: WildLarkTaskHarnessModel[]
    status?: string
}

export interface WildLarkTaskHarnessesListResult {
    harnesses: WildLarkTaskHarness[]
}

export type WildLarkTaskSessionStartParams = TaskBinding & {
    thinking?: import("../agentTarget").ThinkingLevel
    harnessId: string
    model?: string
    label?: string
    prompt: string
    cwd?: string
    mode?: "full" | "read-only"
    clientRequestId: string
}

export type WildLarkTaskSessionSendParams = TaskBinding & {
    targetSessionId: string
    prompt?: string
    browserActionOutcome?: {
        requestId: string
        instruction: string
        status: "completed" | "unable"
        reason?: "tab-closed" | "browser-reset" | "browser-stopped" | "browser-disposed"
        userNotes?: string
    }
    permissionMode?: "ask" | "auto" | "yolo"
    clientRequestId: string
}

export type WildLarkTaskSessionStatusParams = TaskBinding & {
    targetSessionId: string
}

export type WildLarkTaskSessionStopParams = WildLarkTaskSessionStatusParams

export interface WildLarkTaskSessionTurn {
    turnId: string
    sessionId: string
    status: string
    clientRequestId?: string
    queuedAt?: string
    startedAt?: string
    completedAt?: string
    error?: string
}

export interface WildLarkTaskSessionTurnResult {
    session: WildLarkTaskSessionManifestEntry
    turn: WildLarkTaskSessionTurn
    queued: boolean
    effectiveDisposition: string
    reused: boolean
    warnings?: string[] | null
}

export interface WildLarkTaskSessionStatusResult {
    session: WildLarkTaskSessionManifestEntry
    turn?: WildLarkTaskSessionTurn
}

export interface WildLarkTaskSessionStopResult {
    sessionId: string
    status: "complete" | "done-with-gaps"
    requested: number
    succeeded: number
    failed: number
    firstErrors: string[]
}

export interface WildLarkTaskSessionReadStats {
    status: "complete" | "done-with-gaps"
    requested: number
    succeeded: number
    failed: number
    truncated: boolean
    firstErrors: string[]
}

export interface WildLarkTaskWorktree {
    worktreeId: string
    path: string
    branch?: string
    head?: string
}

export interface WildLarkTaskSessionsListResult {
    taskId: string
    title?: string
    workspaceId: string
    workspaceRoot: string
    additionalRoots: string[]
    designatedWorktree?: string
    selectedWorkingDirectory?: string
    repository?: {
        root: string
        branch?: string
        worktrees: WildLarkTaskWorktree[]
    }
    sessions: WildLarkTaskSessionManifestEntry[]
    sessionReads: WildLarkTaskSessionReadStats
}

export type WildLarkFollowupCreateParams = ScheduleFollowupCreateParams & Pick<TaskBinding, "taskId" | "workspaceId">
export type WildLarkFollowupListParams = ScheduleFollowupListParams & Pick<TaskBinding, "workspaceId">
export type WildLarkFollowupUpdateParams = ScheduleFollowupUpdateParams & Pick<TaskBinding, "taskId" | "workspaceId">
export type WildLarkFollowupRunNowParams = ScheduleFollowupRunNowParams & Pick<TaskBinding, "taskId" | "workspaceId">
export type WildLarkFollowupCancelParams = ScheduleFollowupCancelParams & Pick<TaskBinding, "taskId" | "workspaceId">
export type WildLarkFollowupCancelAllParams = ScheduleFollowupCancelAllParams & TaskBinding

interface WildLarkMethodMap {
    "wildlark.task.import": {
        params: { sessionId: string; workspaceId: string; existingTaskId?: string }
        result: { taskId: string; workspaceId: string; reused: boolean }
    }
    "wildlark.prompt.compose": {
        params: WildLarkPromptComposeParams
        result: WildLarkPromptComposeResult
    }
    "wildlark.task.turn.start": {
        params: WildLarkTaskTurnStartParams
        result: SessionTurnStartResult
    }
    "wildlark.task.close": {
        params: WildLarkTaskCloseParams
        result: WildLarkTaskCloseResult
    }
    "wildlark.task.sleep": { params: WildLarkTaskSleepParams; result: WildLarkTaskAttentionResult }
    "wildlark.task.wake": { params: TaskBinding; result: WildLarkTaskAttentionResult }
    "wildlark.task.scheduledStart.reconcile": {
        params: WildLarkTaskScheduledStartReconcileParams
        result: WildLarkTaskScheduledStartReconcileResult
    }
    "wildlark.task.delete": {
        params: WildLarkTaskDeleteParams
        result: WildLarkTaskDeleteResult
    }
    "wildlark.task.directory.set": {
        params: WildLarkTaskDirectorySetParams
        result: WildLarkTaskDirectorySetResult
    }
    "wildlark.task.link.set": {
        params: WildLarkTaskLinkSetParams
        result: WildLarkTaskLinkMutationResult
    }
    "wildlark.task.link.remove": {
        params: WildLarkTaskLinkRemoveParams
        result: WildLarkTaskLinkMutationResult
    }
    "wildlark.task.link.list": {
        params: WildLarkTaskLinkListParams
        result: WildLarkTaskLinkListResult
    }
    "wildlark.task.pr.read": {
        params: WildLarkTaskPullRequestReadParams
        result: WildLarkTaskPullRequestReadResult
    }
    "wildlark.task.pr.automation.set": {
        params: WildLarkTaskPullRequestAutomationSetParams
        result: WildLarkTaskPullRequestAutomationResult
    }
    "wildlark.task.pr.automation.stop": {
        params: WildLarkTaskPullRequestAutomationTargetParams
        result: WildLarkTaskPullRequestAutomationStopResult
    }
    "wildlark.task.pr.automation.retry": {
        params: WildLarkTaskPullRequestAutomationTargetParams
        result: WildLarkTaskPullRequestAutomationResult
    }
    "wildlark.externalWatch.status": {
        params: Record<string, never>
        result: WildLarkExternalWatchStatusResult
    }
    "wildlark.task.harnesses.list": {
        params: WildLarkTaskHarnessesListParams
        result: WildLarkTaskHarnessesListResult
    }
    "wildlark.task.sessions.list": {
        params: WildLarkTaskSessionsListParams
        result: WildLarkTaskSessionsListResult
    }
    "wildlark.task.delegate.set": {
        params: TaskBinding & { target: import("../agentTarget").AgentTarget | null }
        result: { target: import("../agentTarget").AgentTarget | null }
    }
    "wildlark.task.handoff": {
        params: WildLarkTaskHandoffParams
        result: WildLarkTaskSessionTurnResult
    }
    "wildlark.task.handoff.cancel": {
        params: TaskBinding & { clientRequestId: string }
        result: { taskId: string; cancelled: boolean }
    }
    "wildlark.task.session.start": {
        params: WildLarkTaskSessionStartParams
        result: WildLarkTaskSessionTurnResult
    }
    "wildlark.task.session.send": {
        params: WildLarkTaskSessionSendParams
        result: WildLarkTaskSessionTurnResult
    }
    "wildlark.task.session.status": {
        params: WildLarkTaskSessionStatusParams
        result: WildLarkTaskSessionStatusResult
    }
    "wildlark.task.session.stop": {
        params: WildLarkTaskSessionStopParams
        result: WildLarkTaskSessionStopResult
    }
    "wildlark.followup.create": {
        params: WildLarkFollowupCreateParams
        result: ScheduleFollowupCreateResult
    }
    "wildlark.followup.list": {
        params: WildLarkFollowupListParams
        result: ScheduleFollowupListResult
    }
    "wildlark.followup.update": {
        params: WildLarkFollowupUpdateParams
        result: ScheduleFollowupUpdateResult
    }
    "wildlark.followup.runNow": {
        params: WildLarkFollowupRunNowParams
        result: ScheduleFollowupRunNowResult
    }
    "wildlark.followup.cancel": {
        params: WildLarkFollowupCancelParams
        result: ScheduleFollowupCancelResult
    }
    "wildlark.followup.cancelAll": {
        params: WildLarkFollowupCancelAllParams
        result: ScheduleFollowupCancelAllResult
    }
    "wildlark.task.propose": {
        params: WildLarkTaskProposeParams
        result: WildLarkTaskProposeResult
    }
    "wildlark.task.proposal.resolve": {
        params: WildLarkTaskProposalResolveParams
        result: WildLarkTaskProposalResolveResult
    }
}

export interface WildLarkTaskProposalAuthor {
    principalId: string
    kind: string
    displayName: string
    glyph?: string
    color?: string
}

export type WildLarkTaskProposalStatus = "pending" | "approved" | "declined" | "withdrawn"

export interface WildLarkTaskProposal {
    id: string
    input: string
    status: WildLarkTaskProposalStatus
    by: WildLarkTaskProposalAuthor
    createdAt: string
    clientRequestId?: string
    resolvedAt?: string
    resolvedBy?: WildLarkTaskProposalAuthor
    turnId?: string
    note?: string
}

export interface WildLarkTaskProposeParams {
    taskId: string
    workspaceId: string
    input: string
    clientRequestId?: string
}

export interface WildLarkTaskProposeResult {
    proposal: WildLarkTaskProposal
    reused: boolean
    pending: number
}

export interface WildLarkTaskProposalResolveParams {
    taskId: string
    workspaceId: string
    proposalId: string
    outcome: "approved" | "declined" | "withdrawn"
    turnId?: string
    note?: string
}

export interface WildLarkTaskProposalResolveResult {
    proposal: WildLarkTaskProposal
    changed: boolean
    pending: number
}

export type WildLarkMethod = keyof WildLarkMethodMap
export type WildLarkMethodParams<M extends WildLarkMethod> = WildLarkMethodMap[M]["params"]
export type WildLarkMethodResult<M extends WildLarkMethod> = WildLarkMethodMap[M]["result"]

export function requestWildLarkMethod<M extends WildLarkMethod>(
    method: M,
    params: WildLarkMethodParams<M>,
    options?: NestRequestOptions,
    client: NestClient = nestClient
): Promise<WildLarkMethodResult<M>> {
    return client.requestModuleMethod<WildLarkMethodResult<M>>(method, params, options)
}
